import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { API } from "../config";

// ─── Date validation helper ───
const isValidDate = (mm, dd, yyyy) => {
  const m = parseInt(mm, 10);
  const d = parseInt(dd, 10);
  const y = parseInt(yyyy, 10);
  if (!m || !d || !y) return false;
  if (m < 1 || m > 12) return false;
  if (y < 1000) return false;
  const daysInMonth = new Date(y, m, 0).getDate();
  if (d < 1 || d > daysInMonth) return false;
  const inputDate = new Date(y, m - 1, d);
  if (inputDate > new Date()) return false;
  return true;
};

// ─── File validation helper ───
const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".csv",
  ".xls",
  ".xlsx",
  ".doc",
  ".docx",
];

const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const isValidFile = (file) => {
  const ext = "." + file.name.split(".").pop().toLowerCase();
  const extValid = ALLOWED_EXTENSIONS.includes(ext);
  const mimeValid =
    !file.type || file.type === "" || ALLOWED_MIME_TYPES.includes(file.type);
  return extValid && mimeValid;
};

// ─── Input formatters ───

const DateInput = ({ id, name, required, className }) => {
  const [display, setDisplay] = useState("");
  const inputRef = useRef(null);
  const formatMMDDYYYY = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}-${dd}`;
    return `${mm}-${dd}-${yyyy}`;
  };
  const handleChange = (e) => {
    const formatted = formatMMDDYYYY(e.target.value);
    setDisplay(formatted);
    const el = inputRef.current;
    if (!el) return;
    const parts = formatted.split("-");
    if (parts.length === 3 && parts[2]?.length === 4) {
      el.setCustomValidity(
        !isValidDate(parts[0], parts[1], parts[2])
          ? "Please enter a valid date (MM-DD-YYYY)"
          : ""
      );
    } else {
      el.setCustomValidity("");
    }
  };
  return (
    <input
      ref={inputRef}
      type="text"
      id={id}
      name={name}
      required={required}
      value={display}
      onChange={handleChange}
      placeholder="MM-DD-YYYY"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={10}
      pattern="\d{2}-\d{2}-\d{4}"
      title="Please enter a valid date (MM-DD-YYYY)"
      onInvalid={(e) =>
        e.target.setCustomValidity("Please enter a valid date (MM-DD-YYYY)")
      }
    />
  );
};

const EINInput = ({ id, name, required, className }) => {
  const [display, setDisplay] = useState("");
  const formatEIN = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 9);
    if (digits.length <= 2) return digits.slice(0, 2);
    return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
  };
  return (
    <input
      type="text"
      id={id}
      name={name}
      required={required}
      value={display}
      onChange={(e) => setDisplay(formatEIN(e.target.value))}
      placeholder="XX-XXXXXXX"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={10}
      pattern="\d{2}-\d{7}"
      title="Please enter a complete EIN (XX-XXXXXXX)"
      onInvalid={(e) =>
        e.target.setCustomValidity("Please enter a complete EIN (XX-XXXXXXX)")
      }
      onInput={(e) => e.target.setCustomValidity("")}
    />
  );
};

const SSNInput = ({ id, name, required, className }) => {
  const [display, setDisplay] = useState("");
  const formatSSN = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 9);
    if (digits.length <= 3) return digits.slice(0, 3);
    if (digits.length <= 5)
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  };
  return (
    <input
      type="text"
      id={id}
      name={name}
      required={required}
      value={display}
      onChange={(e) => setDisplay(formatSSN(e.target.value))}
      placeholder="XXX-XX-XXXX"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={11}
      pattern="\d{3}-\d{2}-\d{4}"
      title="Please enter a complete SSN (XXX-XX-XXXX)"
      onInvalid={(e) =>
        e.target.setCustomValidity("Please enter a complete SSN (XXX-XX-XXXX)")
      }
      onInput={(e) => e.target.setCustomValidity("")}
    />
  );
};

const PhoneInput = ({
  id,
  name,
  required,
  className,
  defaultValue,
  onValueChange,
}) => {
  const [display, setDisplay] = useState(defaultValue || "");
  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    if (digits.length <= 3)
      return digits.length > 0 ? `(${digits.slice(0, 3)}` : "";
    if (digits.length <= 6)
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
      6,
      10
    )}`;
  };
  return (
    <input
      type="text"
      id={id}
      name={name}
      required={required}
      value={display}
      onChange={(e) => {
        const formatted = formatPhone(e.target.value);
        setDisplay(formatted);
        if (onValueChange) onValueChange(formatted);
      }}
      placeholder="(XXX) XXX-XXXX"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={14}
      pattern="\(\d{3}\) \d{3}-\d{4}"
      title="Please enter a complete phone number (XXX) XXX-XXXX"
      onInvalid={(e) =>
        e.target.setCustomValidity(
          "Please enter a complete phone number (XXX) XXX-XXXX"
        )
      }
      onInput={(e) => e.target.setCustomValidity("")}
    />
  );
};

const NumericInput = ({
  id,
  name,
  required,
  className,
  maxLength,
  placeholder,
}) => {
  const [value, setValue] = useState("");
  return (
    <input
      type="text"
      id={id}
      name={name}
      required={required}
      value={value}
      onChange={(e) =>
        setValue(e.target.value.replace(/\D/g, "").slice(0, maxLength || 10))
      }
      placeholder={placeholder}
      inputMode="numeric"
      autoComplete="off"
      className={className}
      maxLength={maxLength}
      onInvalid={(e) => e.target.setCustomValidity("This field is required")}
      onInput={(e) => e.target.setCustomValidity("")}
    />
  );
};

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];

const StateSelect = ({ id, name, required, className }) => (
  <select
    id={id}
    name={name}
    required={required}
    className={className + " appearance-none"}
  >
    <option value="">Please Select</option>
    {US_STATES.map((s) => (
      <option key={s} value={s}>
        {s}
      </option>
    ))}
  </select>
);

// ─── Main Wizard ───

const Apply = () => {
  const [currentStep, setCurrentStep] = useState(1);

  // ── Step 1 state: Contact Info ──
  const [step1Info, setStep1Info] = useState({
    businessLegalName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // ── Step 1 state: Document Upload ──
  const [bankStatements, setBankStatements] = useState({
    month1: null,
    month2: null,
    month3: null,
    month4: null,
  });
  const [otherFiles, setOtherFiles] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const otherFilesRef = useRef(null);
  const step1FormRef = useRef(null);
  const MAX_SIZE = 500 * 1024 * 1024; // 500MB — files go direct to S3
  const acceptedTypes = ".pdf,.png,.jpg,.jpeg,.csv,.xls,.xlsx,.doc,.docx";

  // ── Step 2 state: Application Form ──
  const [hasSecondOwner, setHasSecondOwner] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const formRef = useRef(null);
  const signatureRef1 = useRef(null);
  const signatureRef2 = useRef(null);
  const [signature1, setSignature1] = useState(null);
  const [signature2, setSignature2] = useState(null);
  const isDrawingRef = useRef(false);

  // ── Signature canvas setup ──
  const setupHiDPICanvas = (canvas) => {
    if (!canvas) return null;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = true;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.miterLimit = 2;
    return ctx;
  };

  useEffect(() => {
    if (currentStep === 2) {
      const timer = setTimeout(() => {
        if (signatureRef1.current) setupHiDPICanvas(signatureRef1.current);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  useEffect(() => {
    if (hasSecondOwner) {
      const timer = setTimeout(() => {
        if (signatureRef2.current) setupHiDPICanvas(signatureRef2.current);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hasSecondOwner]);

  const startDrawing = (e, canvasRef) => {
    if (e.touches) e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    ctx.strokeStyle = "#1e3a5f";
    ctx.lineWidth = e.touches ? 3.2 : 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
    isDrawingRef.current = true;
  };

  const draw = (e, canvasRef) => {
    if (!isDrawingRef.current) return;
    if (e.touches) e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = e.touches ? 3.2 : 2.5;
    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = (canvasRef, setSignature) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    setSignature(canvasRef.current.toDataURL("image/png"));
  };

  const clearSignature = (canvasRef, setSignature) => {
    const ctx = setupHiDPICanvas(canvasRef.current);
    const rect = canvasRef.current.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setSignature(null);
  };

  // ── Step 1: Document helpers ──
  const calculateTotalSize = (statements, others) => {
    let total = 0;
    Object.values(statements).forEach((f) => {
      if (f) total += f.size;
    });
    others.forEach((f) => {
      total += f.size;
    });
    return total;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleBankStatementChange = (month, e) => {
    const file = e.target.files[0] || null;
    if (file && !isValidFile(file)) {
      alert(
        `"${file.name}" is not an accepted file type. Please upload PDF, PNG, JPG, CSV, Excel, or Word files only.`
      );
      e.target.value = "";
      return;
    }
    const ns = { ...bankStatements, [month]: file };
    setBankStatements(ns);
    setTotalSize(calculateTotalSize(ns, otherFiles));
  };

  const removeBankStatement = (month) => {
    const ns = { ...bankStatements, [month]: null };
    setBankStatements(ns);
    setTotalSize(calculateTotalSize(ns, otherFiles));
  };

  const handleOtherFilesChange = (e) => {
    const incoming = Array.from(e.target.files);
    const valid = [];
    const rejected = [];
    incoming.forEach((file) => {
      if (isValidFile(file)) {
        valid.push(file);
      } else {
        rejected.push(file.name);
      }
    });
    if (rejected.length > 0) {
      alert(
        `These files were rejected (unsupported type):\n${rejected.join(
          "\n"
        )}\n\nAccepted: PDF, PNG, JPG, CSV, Excel, Word`
      );
    }
    if (valid.length > 0) {
      const nf = [...otherFiles, ...valid];
      setOtherFiles(nf);
      setTotalSize(calculateTotalSize(bankStatements, nf));
    }
    e.target.value = "";
  };

  const removeOtherFile = (index) => {
    const nf = otherFiles.filter((_, i) => i !== index);
    setOtherFiles(nf);
    setTotalSize(calculateTotalSize(bankStatements, nf));
  };

  const getContentType = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    const types = {
      pdf: "application/pdf",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      csv: "text/csv",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    };
    return types[ext] || "application/octet-stream";
  };

  const hasAnyFiles = () => {
    return (
      Object.values(bankStatements).some((f) => f !== null) ||
      otherFiles.length > 0
    );
  };

  // ── Step 1: Validate contact info + files, then move to Step 2 (no upload yet) ──
  const handleDocsContinue = () => {
    const step1Form = step1FormRef.current;
    if (step1Form && !step1Form.checkValidity()) {
      step1Form.reportValidity();
      return;
    }
    if (!hasAnyFiles()) {
      alert("Please upload at least one bank statement before continuing.");
      return;
    }
    if (totalSize > MAX_SIZE) {
      alert("Total file size exceeds 500MB. Please remove some files.");
      return;
    }

    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Real-time error clearing
  const clearFieldError = (el) => {
    if (!el || !el.validity) return;
    if (el.type === "radio") {
      const groupWrapper = el.closest(".mt-2");
      if (groupWrapper && groupWrapper.hasAttribute("data-error")) {
        groupWrapper.removeAttribute("data-error");
        const msg = groupWrapper.querySelector(".field-error-msg");
        if (msg) msg.remove();
      }
      return;
    }
    if (!el.hasAttribute("data-error") || !el.validity.valid) return;
    el.removeAttribute("data-error");
    el.style.borderColor = "";
    el.style.backgroundColor = "";
    const msg = el.parentNode.querySelector(".field-error-msg");
    if (msg) msg.remove();
    if (el.type === "checkbox") {
      const wrapper = el.closest("label");
      if (wrapper) {
        wrapper.removeAttribute("data-error");
        wrapper.style.border = "";
        wrapper.style.backgroundColor = "";
        wrapper.style.padding = "";
        wrapper.style.borderRadius = "";
      }
    }
  };

  // ── Step 2: Upload files to S3, then submit application + doc notification together ──
  const handleAppSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitted(true);

    const form = formRef.current;

    form.querySelectorAll("[data-error]").forEach((el) => {
      el.removeAttribute("data-error");
      el.style.borderColor = "";
      el.style.backgroundColor = "";
      el.style.border = "";
      el.style.padding = "";
      el.style.borderRadius = "";
    });
    form.querySelectorAll(".field-error-msg").forEach((el) => el.remove());

    if (!form.checkValidity()) {
      const invalidFields = form.querySelectorAll(
        "input:invalid, select:invalid"
      );
      const handledRadioGroups = new Set();
      invalidFields.forEach((el) => {
        if (el.type === "radio") {
          if (handledRadioGroups.has(el.name)) return;
          handledRadioGroups.add(el.name);
          const groupWrapper = el.closest(".mt-2");
          if (groupWrapper) {
            groupWrapper.setAttribute("data-error", "true");
            const msg = document.createElement("p");
            msg.className = "field-error-msg";
            msg.style.cssText = "color:#ef4444;font-size:12px;margin-top:8px;";
            msg.textContent = "Please select an entity type";
            groupWrapper.appendChild(msg);
          }
          return;
        }
        el.setAttribute("data-error", "true");
        el.style.borderColor = "#ef4444";
        el.style.backgroundColor = "#fef2f2";

        const msg = document.createElement("p");
        msg.className = "field-error-msg";
        msg.style.cssText = "color:#ef4444;font-size:12px;margin-top:4px;";

        if (el.type === "checkbox") {
          const wrapper = el.closest("label");
          if (wrapper) {
            wrapper.setAttribute("data-error", "true");
            wrapper.style.backgroundColor = "#fef2f2";
            wrapper.style.border = "1px solid #ef4444";
            wrapper.style.borderRadius = "12px";
            wrapper.style.padding = "12px";
          }
          msg.textContent = "Please check this box to proceed";
        } else {
          msg.textContent = el.validationMessage || "This field is required";
        }
        el.parentNode.appendChild(msg);
      });
      const firstInvalid = invalidFields[0];
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        firstInvalid.focus({ preventScroll: true });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // ── Build application data ──
      const formData = new FormData(e.target);
      const data = Object.fromEntries(formData.entries());
      data.signature1 = signature1;
      data.signature2 = hasSecondOwner ? signature2 : null;
      data.hasSecondOwner = hasSecondOwner;

      // ── Build file list for S3 ──
      const allFiles = [];
      Object.entries(bankStatements).forEach(([month, file]) => {
        if (file) {
          allFiles.push({
            file,
            filename: `BankStatement-${month}-${file.name}`,
          });
        }
      });
      otherFiles.forEach((file) => {
        allFiles.push({ file, filename: file.name });
      });

      // ── Step A: Get presigned upload URLs from S3 ──
      const urlResponse = await fetch(
        API.GET_UPLOAD_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            files: allFiles.map((f) => ({
              filename: f.filename,
              contentType: getContentType(f.filename),
            })),
          }),
        }
      );
      if (!urlResponse.ok) throw new Error("Failed to get upload URLs");
      const { uploadUrls } = await urlResponse.json();

      // ── Step B: Upload files directly to S3 in parallel ──
      await Promise.all(
        uploadUrls.map((urlInfo, index) =>
          fetch(urlInfo.uploadUrl, {
            method: "PUT",
            headers: {
              "Content-Type": getContentType(allFiles[index].filename),
            },
            body: allFiles[index].file,
          })
        )
      );

      // ── Step C: Fire application + doc notification in parallel ──
      const docPayload = {
        businessLegalName: step1Info.businessLegalName || data.businessLegalName || "Unknown Business",
        firstName: step1Info.firstName || data.owner1FirstName || "",
        lastName: step1Info.lastName || data.owner1LastName || "",
        email: step1Info.email || data.owner1Email || "",
        phone: step1Info.phone || data.owner1Phone || "",
        subject: "Document Upload",
        attachments: uploadUrls.map((urlInfo) => ({
          filename: urlInfo.filename,
          key: urlInfo.key,
        })),
      };

      const [appResponse, docResponse] = await Promise.all([
        fetch(
          API.SUBMIT_APPLICATION,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          }
        ),
        fetch(
          API.UPLOAD_DOCUMENTS,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(docPayload),
          }
        ),
      ]);

      if (!appResponse.ok) throw new Error("Application submission failed");

      if (!docResponse.ok) {
        // Application succeeded but documents email failed — tell the user
        window.location.href = "/thank-you?docs=failed";
      } else {
        window.location.href = "/thank-you";
      }
    } catch (error) {
      alert("Oops! Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  // ── Shared styles ──
  const inputClasses =
    "mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition";
  const labelClasses = "block text-sm font-medium text-slate-700";
  const sectionClasses =
    "bg-white rounded-2xl p-6 sm:p-8 shadow-sm ring-1 ring-slate-100";

  // ── Step Indicator ──
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-0 mb-10">
      <div className="flex items-center">
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold transition-all ${
            currentStep > 1
              ? "bg-green-500 text-white"
              : "bg-blue-600 text-white ring-4 ring-blue-100"
          }`}
        >
          {currentStep > 1 ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={3}
              stroke="currentColor"
              className="w-5 h-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m4.5 12.75 6 6 9-13.5"
              />
            </svg>
          ) : (
            "1"
          )}
        </div>
        <span
          className={`ml-2 text-sm font-medium hidden sm:inline ${
            currentStep > 1 ? "text-green-600" : "text-blue-700"
          }`}
        >
          Documents
        </span>
      </div>
      <div
        className={`w-12 sm:w-20 h-0.5 mx-3 ${
          currentStep > 1 ? "bg-green-400" : "bg-slate-200"
        }`}
      />
      <div className="flex items-center">
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold transition-all ${
            currentStep === 2
              ? "bg-blue-600 text-white ring-4 ring-blue-100"
              : "bg-slate-200 text-slate-500"
          }`}
        >
          2
        </div>
        <span
          className={`ml-2 text-sm font-medium hidden sm:inline ${
            currentStep === 2 ? "text-blue-700" : "text-slate-400"
          }`}
        >
          Application
        </span>
      </div>
    </div>
  );

  // ═══════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════

  return (
    <>
      <SEO
        title={currentStep === 1 ? "Upload Documents" : "Apply for Funding"}
        description="Complete your business funding application with Lloyd Funding."
        canonical="/apply"
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-3xl px-4 pt-20 pb-8 sm:px-6 sm:pt-24 sm:pb-10 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              {currentStep === 1 ? (
                <>
                  {" "}
                  Upload <span className="text-blue-700">Documents</span>{" "}
                </>
              ) : (
                <>
                  {" "}
                  Funding <span className="text-blue-700">
                    Application
                  </span>{" "}
                </>
              )}
            </h1>
            <p className="mt-4 text-lg text-slate-600 text-pretty">
              {currentStep === 1
                ? "Enter your info and upload your last 3 bank statements to get started."
                : "Complete the form below to finish your application."}
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-slate-50 pb-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <StepIndicator />

          {/* ═══ STEP 1: CONTACT INFO + DOCUMENT UPLOAD ═══ */}
          {currentStep === 1 && (
            <div className="space-y-8">
              {/* Contact Information */}
              <form ref={step1FormRef} noValidate>
                <div className={sectionClasses}>
                  <h2 className="text-xl font-semibold text-slate-800 mb-6">
                    Contact Information
                  </h2>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="s1BusinessLegalName"
                        className={labelClasses}
                      >
                        Business Legal Name{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="s1BusinessLegalName"
                        required
                        value={step1Info.businessLegalName}
                        onChange={(e) =>
                          setStep1Info({
                            ...step1Info,
                            businessLegalName: e.target.value,
                          })
                        }
                        className={inputClasses}
                        onInvalid={(e) =>
                          e.target.setCustomValidity(
                            "Please enter your business name"
                          )
                        }
                        onInput={(e) => e.target.setCustomValidity("")}
                      />
                    </div>
                    <div>
                      <label htmlFor="s1FirstName" className={labelClasses}>
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="s1FirstName"
                        required
                        value={step1Info.firstName}
                        onChange={(e) =>
                          setStep1Info({
                            ...step1Info,
                            firstName: e.target.value,
                          })
                        }
                        className={inputClasses}
                        onInvalid={(e) =>
                          e.target.setCustomValidity(
                            "Please enter your first name"
                          )
                        }
                        onInput={(e) => e.target.setCustomValidity("")}
                      />
                    </div>
                    <div>
                      <label htmlFor="s1LastName" className={labelClasses}>
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="s1LastName"
                        required
                        value={step1Info.lastName}
                        onChange={(e) =>
                          setStep1Info({
                            ...step1Info,
                            lastName: e.target.value,
                          })
                        }
                        className={inputClasses}
                        onInvalid={(e) =>
                          e.target.setCustomValidity(
                            "Please enter your last name"
                          )
                        }
                        onInput={(e) => e.target.setCustomValidity("")}
                      />
                    </div>
                    <div>
                      <label htmlFor="s1Email" className={labelClasses}>
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="s1Email"
                        required
                        value={step1Info.email}
                        onChange={(e) =>
                          setStep1Info({ ...step1Info, email: e.target.value })
                        }
                        pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                        title="Please enter a valid email (e.g., name@example.com)"
                        className={inputClasses}
                        onInvalid={(e) =>
                          e.target.setCustomValidity(
                            "Please enter a valid email (e.g., name@example.com)"
                          )
                        }
                        onInput={(e) => e.target.setCustomValidity("")}
                      />
                    </div>
                    <div>
                      <label htmlFor="s1Phone" className={labelClasses}>
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <PhoneInput
                        id="s1Phone"
                        name="s1Phone"
                        required={true}
                        className={inputClasses}
                        onValueChange={(val) =>
                          setStep1Info({ ...step1Info, phone: val })
                        }
                      />
                    </div>
                  </div>
                </div>
              </form>

              {/* Bank Statements */}
              <div className={sectionClasses}>
                <h2 className="text-xl font-semibold text-slate-800 mb-2">
                  Bank Statements
                </h2>
                <p className="text-sm text-amber-600 mb-6 flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                    />
                  </svg>
                  4 months required for businesses registered in CA and NY
                </p>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                  {["month1", "month2", "month3", "month4"].map(
                    (month, index) => (
                      <div key={month}>
                        {bankStatements[month] ? (
                          <div className="flex flex-col items-center justify-between rounded-xl border border-slate-300 bg-white p-3 h-24">
                            <span className="text-xs text-slate-600 truncate max-w-full text-center">
                              {bankStatements[month].name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeBankStatement(month)}
                              className="text-red-500 hover:text-red-700 mt-2"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={2}
                                stroke="currentColor"
                                className="w-5 h-5"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M6 18 18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <label className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white h-24 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="w-6 h-6 text-slate-400"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                              />
                            </svg>
                            <span className="mt-1 text-xs text-slate-500">
                              Month {index + 1}
                            </span>
                            <input
                              type="file"
                              accept={acceptedTypes}
                              onChange={(e) =>
                                handleBankStatementChange(month, e)
                              }
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                    )
                  )}
                </div>

                {/* Additional documents */}
                <label className="flex items-center justify-center w-full rounded-xl border-2 border-dashed border-slate-300 bg-white px-4 py-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  <div className="text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8 mx-auto text-slate-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                      />
                    </svg>
                    <span className="mt-2 block text-sm text-slate-500">
                      Click to upload additional documents
                    </span>
                    <span className="mt-1 block text-xs text-slate-400">
                      PDF, PNG, JPG, CSV, Excel, Word
                    </span>
                  </div>
                  <input
                    type="file"
                    ref={otherFilesRef}
                    accept={acceptedTypes}
                    multiple
                    onChange={handleOtherFilesChange}
                    className="hidden"
                  />
                </label>

                {/* Uploaded files list */}
                {otherFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {otherFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded-xl border border-slate-300 bg-white px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-5 h-5 text-slate-400"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                            />
                          </svg>
                          <div>
                            <p className="text-sm text-slate-600 truncate max-w-[200px] sm:max-w-[300px]">
                              {file.name}
                            </p>
                            <p className="text-xs text-slate-400">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeOtherFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18 18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* File size warning */}
              {totalSize > 0 && (
                <div
                  className={`rounded-xl p-4 ${
                    totalSize > MAX_SIZE
                      ? "bg-red-50 text-red-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {totalSize > MAX_SIZE && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                        />
                      </svg>
                    )}
                    <span className="text-sm font-medium">
                      Total file size: {formatFileSize(totalSize)} / 500 MB
                      {totalSize > MAX_SIZE &&
                        " — Please remove some files to continue"}
                    </span>
                  </div>
                </div>
              )}

              {/* Continue Button */}
              <button
                type="button"
                onClick={handleDocsContinue}
                disabled={totalSize > MAX_SIZE}
                className="w-full rounded-xl bg-blue-600 px-8 py-4 text-base font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Application →
              </button>
            </div>
          )}

          {/* ═══ STEP 2: APPLICATION FORM ═══ */}
          {currentStep === 2 && (
            <>
              {/* Info banner */}
              <div className="mb-8 rounded-2xl bg-blue-50 p-4 ring-1 ring-blue-200">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={3}
                      stroke="currentColor"
                      className="w-4 h-4 text-white"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m4.5 12.75 6 6 9-13.5"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-800">
                      Documents ready!
                    </p>
                    <p className="text-xs text-blue-600">
                      Complete the application below. Everything will be submitted together.
                    </p>
                  </div>
                </div>
              </div>

              <form
                ref={formRef}
                onSubmit={handleAppSubmit}
                noValidate
                onInput={(e) => clearFieldError(e.target)}
                onChange={(e) => clearFieldError(e.target)}
                className={`space-y-8 ${formSubmitted ? "form-submitted" : ""}`}
              >
                {/* Business Information */}
                <div className={sectionClasses}>
                  <h2 className="text-xl font-semibold text-slate-800 mb-6">
                    Business Information
                  </h2>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="businessLegalName"
                        className={labelClasses}
                      >
                        Business Legal Name{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="businessLegalName"
                        name="businessLegalName"
                        required
                        defaultValue={step1Info.businessLegalName}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="businessDBA" className={labelClasses}>
                        Business DBA
                      </label>
                      <input
                        type="text"
                        id="businessDBA"
                        name="businessDBA"
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="ein" className={labelClasses}>
                        EIN/Tax ID <span className="text-red-500">*</span>
                      </label>
                      <EINInput
                        id="ein"
                        name="ein"
                        required={true}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="industry" className={labelClasses}>
                        Industry <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="industry"
                        name="industry"
                        required
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="businessStartDate"
                        className={labelClasses}
                      >
                        Business Start Date{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        id="businessStartDate"
                        name="businessStartDate"
                        required={true}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>
                        Entity Type <span className="text-red-500">*</span>
                      </label>
                      <div className="mt-2 grid grid-cols-2 gap-3">
                        {["LLC", "Corp", "Sole Prop", "Partnership"].map(
                          (type) => (
                            <label
                              key={type}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <input
                                type="radio"
                                name="entityType"
                                value={type}
                                required
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300"
                              />
                              <span className="text-sm text-slate-700">
                                {type}
                              </span>
                            </label>
                          )
                        )}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="businessStreet" className={labelClasses}>
                        Business Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="businessStreet"
                        name="businessStreet"
                        required
                        placeholder="Street Address"
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="businessCity" className={labelClasses}>
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="businessCity"
                        name="businessCity"
                        required
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="businessState" className={labelClasses}>
                        State <span className="text-red-500">*</span>
                      </label>
                      <StateSelect
                        id="businessState"
                        name="businessState"
                        required={true}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="businessZip" className={labelClasses}>
                        Zip Code <span className="text-red-500">*</span>
                      </label>
                      <NumericInput
                        id="businessZip"
                        name="businessZip"
                        required={true}
                        maxLength={5}
                        placeholder="XXXXX"
                        className={inputClasses}
                      />
                    </div>
                  </div>
                </div>

                {/* Owner's Information */}
                <div className={sectionClasses}>
                  <h2 className="text-xl font-semibold text-slate-800 mb-6">
                    Owner's Information
                  </h2>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="owner1FirstName" className={labelClasses}>
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="owner1FirstName"
                        name="owner1FirstName"
                        required
                        defaultValue={step1Info.firstName}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1LastName" className={labelClasses}>
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="owner1LastName"
                        name="owner1LastName"
                        required
                        defaultValue={step1Info.lastName}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1SSN" className={labelClasses}>
                        Social Security Number{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <SSNInput
                        id="owner1SSN"
                        name="owner1SSN"
                        required={true}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1Ownership" className={labelClasses}>
                        Ownership Percentage (%){" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <NumericInput
                        id="owner1Ownership"
                        name="owner1Ownership"
                        required={true}
                        maxLength={3}
                        placeholder="%"
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1DOB" className={labelClasses}>
                        Date of Birth <span className="text-red-500">*</span>
                      </label>
                      <DateInput
                        id="owner1DOB"
                        name="owner1DOB"
                        required={true}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1FICO" className={labelClasses}>
                        FICO Score
                      </label>
                      <NumericInput
                        id="owner1FICO"
                        name="owner1FICO"
                        required={false}
                        maxLength={3}
                        placeholder="e.g., 650"
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1Phone" className={labelClasses}>
                        Cell Phone <span className="text-red-500">*</span>
                      </label>
                      <PhoneInput
                        id="owner1Phone"
                        name="owner1Phone"
                        required={true}
                        className={inputClasses}
                        defaultValue={step1Info.phone}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1Email" className={labelClasses}>
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="owner1Email"
                        name="owner1Email"
                        required
                        defaultValue={step1Info.email}
                        pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                        title="Please enter a valid email (e.g., name@example.com)"
                        onInvalid={(e) =>
                          e.target.setCustomValidity(
                            "Please enter a valid email (e.g., name@example.com)"
                          )
                        }
                        onInput={(e) => e.target.setCustomValidity("")}
                        className={inputClasses}
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        *Application confirmation will be sent to this email
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="owner1Street" className={labelClasses}>
                        Home Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="owner1Street"
                        name="owner1Street"
                        required
                        placeholder="Street Address"
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1City" className={labelClasses}>
                        City <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="owner1City"
                        name="owner1City"
                        required
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1State" className={labelClasses}>
                        State <span className="text-red-500">*</span>
                      </label>
                      <StateSelect
                        id="owner1State"
                        name="owner1State"
                        required={true}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label htmlFor="owner1Zip" className={labelClasses}>
                        Zip Code <span className="text-red-500">*</span>
                      </label>
                      <NumericInput
                        id="owner1Zip"
                        name="owner1Zip"
                        required={true}
                        maxLength={5}
                        placeholder="XXXXX"
                        className={inputClasses}
                      />
                    </div>
                  </div>
                </div>

                {/* 2nd Owner Toggle */}
                <div className={sectionClasses}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasSecondOwner}
                      onChange={(e) => setHasSecondOwner(e.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-base font-medium text-slate-800">
                      Add 2nd Owner Information
                    </span>
                  </label>
                  {hasSecondOwner && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <h3 className="text-lg font-semibold text-slate-800 mb-5">
                        2nd Owner's Information
                      </h3>
                      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                          <label
                            htmlFor="owner2FirstName"
                            className={labelClasses}
                          >
                            First Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="owner2FirstName"
                            name="owner2FirstName"
                            required={hasSecondOwner}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="owner2LastName"
                            className={labelClasses}
                          >
                            Last Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="owner2LastName"
                            name="owner2LastName"
                            required={hasSecondOwner}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label htmlFor="owner2SSN" className={labelClasses}>
                            Social Security Number{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <SSNInput
                            id="owner2SSN"
                            name="owner2SSN"
                            required={hasSecondOwner}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="owner2Ownership"
                            className={labelClasses}
                          >
                            Ownership Percentage (%){" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <NumericInput
                            id="owner2Ownership"
                            name="owner2Ownership"
                            required={hasSecondOwner}
                            maxLength={3}
                            placeholder="%"
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label htmlFor="owner2DOB" className={labelClasses}>
                            Date of Birth{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <DateInput
                            id="owner2DOB"
                            name="owner2DOB"
                            required={hasSecondOwner}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label htmlFor="owner2FICO" className={labelClasses}>
                            FICO Score
                          </label>
                          <NumericInput
                            id="owner2FICO"
                            name="owner2FICO"
                            required={false}
                            maxLength={3}
                            placeholder="e.g., 650"
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label htmlFor="owner2Phone" className={labelClasses}>
                            Cell Phone
                          </label>
                          <PhoneInput
                            id="owner2Phone"
                            name="owner2Phone"
                            required={false}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label htmlFor="owner2Email" className={labelClasses}>
                            Email Address
                          </label>
                          <input
                            type="email"
                            id="owner2Email"
                            name="owner2Email"
                            pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                            title="Please enter a valid email (e.g., name@example.com)"
                            onInvalid={(e) =>
                              e.target.setCustomValidity(
                                "Please enter a valid email (e.g., name@example.com)"
                              )
                            }
                            onInput={(e) => e.target.setCustomValidity("")}
                            className={inputClasses}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label
                            htmlFor="owner2Street"
                            className={labelClasses}
                          >
                            Home Address <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="owner2Street"
                            name="owner2Street"
                            required={hasSecondOwner}
                            placeholder="Street Address"
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label htmlFor="owner2City" className={labelClasses}>
                            City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="owner2City"
                            name="owner2City"
                            required={hasSecondOwner}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label htmlFor="owner2State" className={labelClasses}>
                            State <span className="text-red-500">*</span>
                          </label>
                          <StateSelect
                            id="owner2State"
                            name="owner2State"
                            required={hasSecondOwner}
                            className={inputClasses}
                          />
                        </div>
                        <div>
                          <label htmlFor="owner2Zip" className={labelClasses}>
                            Zip Code <span className="text-red-500">*</span>
                          </label>
                          <NumericInput
                            id="owner2Zip"
                            name="owner2Zip"
                            required={hasSecondOwner}
                            maxLength={5}
                            placeholder="XXXXX"
                            className={inputClasses}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Signatures */}
                <div className={sectionClasses}>
                  <h2 className="text-xl font-semibold text-slate-800 mb-6">
                    Owner's Signature
                  </h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className={labelClasses + " mb-2"}>
                        1. Owner's Signature{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <canvas
                        ref={signatureRef1}
                        className="w-full h-40 border border-slate-200 rounded-xl bg-white cursor-crosshair touch-none"
                        onMouseDown={(e) => startDrawing(e, signatureRef1)}
                        onMouseMove={(e) => draw(e, signatureRef1)}
                        onMouseUp={() =>
                          stopDrawing(signatureRef1, setSignature1)
                        }
                        onMouseLeave={() => (isDrawingRef.current = false)}
                        onTouchStart={(e) => startDrawing(e, signatureRef1)}
                        onTouchMove={(e) => draw(e, signatureRef1)}
                        onTouchEnd={() =>
                          stopDrawing(signatureRef1, setSignature1)
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          clearSignature(signatureRef1, setSignature1)
                        }
                        className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                      >
                        Clear
                      </button>
                    </div>
                    {hasSecondOwner && (
                      <div>
                        <label className={labelClasses + " mb-2"}>
                          2. Owner's Signature
                        </label>
                        <canvas
                          ref={signatureRef2}
                          className="w-full h-40 border border-slate-200 rounded-xl bg-white cursor-crosshair touch-none"
                          onMouseDown={(e) => startDrawing(e, signatureRef2)}
                          onMouseMove={(e) => draw(e, signatureRef2)}
                          onMouseUp={() =>
                            stopDrawing(signatureRef2, setSignature2)
                          }
                          onMouseLeave={() => (isDrawingRef.current = false)}
                          onTouchStart={(e) => startDrawing(e, signatureRef2)}
                          onTouchMove={(e) => draw(e, signatureRef2)}
                          onTouchEnd={() =>
                            stopDrawing(signatureRef2, setSignature2)
                          }
                        />
                        <button
                          type="button"
                          onClick={() =>
                            clearSignature(signatureRef2, setSignature2)
                          }
                          className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                    <div className={hasSecondOwner ? "sm:col-span-2" : ""}>
                      <label htmlFor="signatureDate" className={labelClasses}>
                        Signature Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        id="signatureDate"
                        name="signatureDate"
                        required
                        defaultValue={new Date().toISOString().split("T")[0]}
                        className={inputClasses + " sm:max-w-xs"}
                      />
                    </div>
                  </div>
                </div>

                {/* Legal Disclaimer */}
                <div className={sectionClasses}>
                  <div className="text-xs leading-relaxed text-slate-500 mt-4">
                    <p className="font-medium text-slate-600 mb-2">
                      Authorization & Consent:
                    </p>
                    <p>
                      By signing above, you authorize Lloyd Funding Group Inc
                      dba Lloyd Funding and its representatives, successors,
                      assigns, and designees ("Recipients") to:
                    </p>
                    <ol className="list-decimal pl-5 mt-2 space-y-1.5">
                      <li>
                        Obtain consumer, personal, business, and investigative
                        reports, including credit inquiries (hard or soft
                        pulls), from agencies including TransUnion, Experian,
                        and Equifax;
                      </li>
                      <li>
                        Obtain credit card processing and bank statements from
                        financial institutions and third parties;
                      </li>
                      <li>
                        Share this application and related information with
                        Recipients for the purposes described herein;
                      </li>
                      <li>
                        Authorize any creditor or financial institution to
                        release your information to Lloyd Funding and
                        Recipients;
                      </li>
                      <li>
                        Contact you by email, phone, and/or text message at the
                        contact information provided, including mobile numbers,
                        even if registered on a Do-Not-Call list, using
                        automated dialing systems, regarding this application,
                        future financing opportunities, and/or other lawful
                        marketing purposes.
                      </li>
                    </ol>
                    <p className="mt-2">
                      Consent to receive text messages is not required for
                      application approval. Message frequency varies. Message
                      and data rates may apply. Reply STOP to opt out. I/We
                      certify that all information provided is complete, true,
                      and accurate. View our{" "}
                      <Link
                        to="/privacy-policy"
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Privacy Policy
                      </Link>{" "}
                      and{" "}
                      <Link
                        to="/terms-of-service"
                        className="text-blue-600 hover:text-blue-700 underline"
                      >
                        Terms of Service
                      </Link>
                      .
                    </p>
                  </div>
                  <div className="mt-6">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="agreeToTerms"
                        required
                        className="mt-1 h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">
                        I have read and agree to the terms above{" "}
                        <span className="text-red-500">*</span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      !signature1 ||
                      (hasSecondOwner && !signature2)
                    }
                    className="w-full rounded-xl bg-blue-600 px-8 py-4 text-base font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg
                          className="animate-spin h-5 w-5"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      "Submit Application"
                    )}
                  </button>
                  <p className="mt-4 text-center text-xs text-slate-500">
                    Your information is encrypted and securely transmitted.
                  </p>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Apply;